'use client';

import type { TutorBundle } from '@peach/types/tutors/tutor-profile.dto.js';
import { transformBundles } from '@/components/products/bundle-config';
import { BookSlotCard } from '../BookSlotCard';
import { tw } from '@tw';
import { IntroEditor, IntroLine } from './IntroEditor';
import s from './sub.module.css';

export const INTRO_STEPS_ID = 'intro-steps';

interface IntroStepsProps {
  /** The catalogue trial; null only for the owner, who is offered to add one. */
  bundle: TutorBundle | null;
  firstName: string;
  tutorUsername: string;
  loggedIn: boolean;
  isOwner: boolean;
  serverNow: number;
  /** The visitor tapped a time in the intro's calendar. */
  onChoose?: () => void;
}

/** Concept E: meet the creator first, then pick a plan. */
export function IntroSteps({
  bundle,
  firstName,
  tutorUsername,
  loggedIn,
  isOwner,
  serverNow,
  onChoose,
}: IntroStepsProps) {
  const steps = bundle && (
    <ol className={s.steps}>
      <li>
        <span className={s.stepNum} aria-hidden="true">
          1
        </span>
        <span>
          <b>Pick a time</b>
          <span>Times shown in your time zone</span>
        </span>
      </li>
      <li>
        <span className={s.stepNum} aria-hidden="true">
          2
        </span>
        <span>
          <b>Talk with {firstName}</b>
          <span>Your goal, and how many calls a week fit</span>
        </span>
      </li>
      <li>
        <span className={tw(s.stepNum, s.stepLast)} aria-hidden="true">
          3
        </span>
        <span>
          <b>Pick your plan</b>
          <span>One or more calls a week, or the membership</span>
        </span>
      </li>
    </ol>
  );

  return (
    <section id={INTRO_STEPS_ID} aria-labelledby="intro-title" className="scroll-mt-header">
      <h2 id="intro-title" className={s.sech}>
        {bundle ? `Meet ${firstName} first` : 'No intro call on this page yet'}
      </h2>
      <div className={s.intro}>
        {bundle && !isOwner && (
          <IntroLine
            duration={bundle.duration}
            price={bundle.price}
            previousPrice={bundle.previousPrice}
            currency={bundle.currency}
          />
        )}
        {isOwner ? (
          // The owner prices the intro where it stands, at rest as a client reads it.
          <IntroEditor
            bundles={transformBundles(bundle ? [bundle] : [])}
            currency={bundle?.currency ?? 'USD'}
            username={tutorUsername}
          >
            {steps}
          </IntroEditor>
        ) : (
          bundle && (
            <>
              {steps}
              <div className="sfv2">
                <BookSlotCard
                  bundle={bundle}
                  tutorUsername={tutorUsername}
                  loggedIn={loggedIn}
                  isOwner={false}
                  firstName={firstName}
                  authSource="calls_block"
                  surface="card"
                  serverNow={serverNow}
                  onChoose={onChoose}
                />
              </div>
            </>
          )
        )}
      </div>
    </section>
  );
}
